import Image from '@tiptap/extension-image';
import { NodeViewWrapper, ReactNodeViewRenderer, type NodeViewProps } from '@tiptap/react';
import { useEffect, useState } from 'react';
import type { EditorContext } from '../../types';

/**
 * Images. Wiki attachments live in the wiki's git repo under /.attachments
 * and need the user's token to read, so relative sources are fetched through
 * the BFF and shown via an object URL. Absolute URLs render directly.
 */

export interface AdoImageOptions {
  ctx: EditorContext | null;
}

const blobCache = new Map<string, string>();

function isRelative(src: string) {
  return !/^(https?:)?\/\//i.test(src) && !src.startsWith('data:') && !src.startsWith('blob:');
}

function ImageView({ node, extension, selected }: NodeViewProps) {
  const ctx = (extension.options as unknown as AdoImageOptions).ctx;
  const src = String(node.attrs.src ?? '');
  const [url, setUrl] = useState<string | null>(isRelative(src) ? blobCache.get(src) ?? null : src);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!isRelative(src)) {
      setUrl(src);
      return;
    }
    if (blobCache.has(src)) {
      setUrl(blobCache.get(src)!);
      return;
    }
    if (!ctx) return;
    let live = true;
    ctx.api
      .getAttachment(ctx.project, ctx.wiki, src)
      .then(blob => {
        const u = URL.createObjectURL(blob);
        blobCache.set(src, u);
        if (live) setUrl(u);
      })
      .catch(() => live && setError(true));
    return () => {
      live = false;
    };
  }, [ctx, src]);

  return (
    <NodeViewWrapper className={`ado-image${selected ? ' is-selected' : ''}`} contentEditable={false} data-drag-handle>
      {error ? (
        <span className="ado-image-missing">Image not found: {src}</span>
      ) : url ? (
        <img src={url} alt={node.attrs.alt ?? ''} title={node.attrs.title ?? undefined} />
      ) : (
        <span className="ado-image-loading">Loading {src}…</span>
      )}
    </NodeViewWrapper>
  );
}

export const AdoImage = Image.extend<AdoImageOptions & Record<string, unknown>>({
  addOptions() {
    return { ...this.parent?.(), ctx: null };
  },
  addNodeView() {
    return ReactNodeViewRenderer(ImageView);
  },
});
